import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LoginForm } from './components/shared/LoginForm';
import { RegisterForm } from './components/shared/RegisterForm';
import { ForgotPasswordForm } from './components/shared/ForgotPasswordForm';
import { ResetPasswordForm } from './components/shared/ResetPasswordForm';
import { ArtistManager } from './components/ArtistManager';
import GetFeedbackTab from './components/GetFeedbackTab';
import GiveFeedbackTab from './components/GiveFeedbackTab';
import MyReviews from './components/MyReviews';
import RequestFeedback from './components/RequestFeedback';
import AdminDashboard from './components/AdminDashboard';
import LandingPage from './components/LandingPage';
import Footer from './components/Footer';
import Header from './components/Header';
import HowItWorks from './components/HowItWorks';
import SubscriptionManager from './components/SubscriptionManager';
import { TermsOfService } from './components/TermsOfService';
import { PrivacyPolicy } from './components/PrivacyPolicy';
import { ArtistSetupCard } from './components/shared/ArtistSetupCard';
import { artistApi } from './services/artistApi';
import { CreateArtistRequest } from './types/artist';
import './components/shared/AuthForm.css';
import './components/GiveFeedbackTab.css';

type TabType = 'my-tracks' | 'upload' | 'give-feedback' | 'my-reviews' | 'account';
type AuthView = 'login' | 'register' | 'forgot-password';

function MainApp() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>(isAuthenticated ? 'give-feedback' : 'account');
  const [authView, setAuthView] = useState<AuthView>('login');
  const [showAdminDashboard, setShowAdminDashboard] = useState(false);
  const [checkingArtists, setCheckingArtists] = useState(false);
  const [needsArtistSetup, setNeedsArtistSetup] = useState(false);
  const [showArtistModal, setShowArtistModal] = useState(false);
  const [artistName, setArtistName] = useState('');
  const [artistUsesAI, setArtistUsesAI] = useState(false);
  const [creatingArtist, setCreatingArtist] = useState(false);
  const [artistError, setArtistError] = useState<string | null>(null);

  // Check if user is admin
  const isAdmin = user?.roles?.includes('Admin') || false;

  // Create artist profile
  const createArtist = async () => {
    if (!user?.id || !artistName.trim()) return;

    try {
      setCreatingArtist(true);
      setArtistError(null);
      const createRequest: CreateArtistRequest = {
        name: artistName.trim(),
        usesAI: artistUsesAI
      };
      
      await artistApi.createArtist(createRequest);
      setShowArtistModal(false);
      setArtistName('');
      setArtistUsesAI(false);
      setNeedsArtistSetup(false);
      
      // Navigate to give-feedback after creating artist
      setActiveTab('give-feedback');
      window.history.replaceState(null, '', '/give-feedback');
    } catch (err) {
      console.error('Failed to create artist:', err);
      setArtistError('Failed to create artist profile. Please try again.');
    } finally {
      setCreatingArtist(false);
    }
  };

  // Check if user has artist profiles
  const checkUserArtists = useCallback(async () => {
    if (!user?.id || !isAuthenticated) {
      setCheckingArtists(false);
      setNeedsArtistSetup(false);
      return;
    }

    try {
      setCheckingArtists(true);
      const userArtists = await artistApi.getArtistsByUserId(user.id);
      
      // If user has no artists, they need to set up an artist profile
      if (userArtists.length === 0) {
        setNeedsArtistSetup(true);
        setShowArtistModal(true);
      } else {
        setNeedsArtistSetup(false);
        setShowArtistModal(false);
        // If user has artists and came from landing page, go to give-feedback
        // BUT: Don't redirect if they're on /account with query parameters (like edit=true)
        const hasAccountQueryParams = location.pathname === '/account' && location.search;
        if ((location.pathname === '/' || location.pathname === '/account') && !hasAccountQueryParams) {
          setActiveTab('give-feedback');
          window.history.replaceState(null, '', '/give-feedback');
        } else if (hasAccountQueryParams) {
          console.log('App - Staying on /account due to query params:', location.search);
        }
      }
    } catch (err) {
      console.error('Failed to load user artists:', err);
      // If we can't load artists, assume none exist and show modal
      setNeedsArtistSetup(true);
      setShowArtistModal(true);
    } finally {
      setCheckingArtists(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isAuthenticated, location.pathname]); 
  // Note: location.search is intentionally not in deps - we read it but don't want to re-run on query param changes

  // Check for artist profiles when user authenticates
  useEffect(() => {
    if (isAuthenticated && !isLoading && user) {
      checkUserArtists();
    }
  }, [isAuthenticated, isLoading, user, checkUserArtists]);

  // Redirect unauthenticated users to / for all routes except /request-feedback, /terms, and /privacy
  useEffect(() => {
    const publicPaths = ['/', '/terms', '/privacy'];
    if (!isLoading && !isAuthenticated && !publicPaths.includes(location.pathname)) {
      navigate('/', { replace: true });
    }
  }, [isLoading, isAuthenticated, location.pathname, navigate]);

  // Update the active tab when authentication status changes
  useEffect(() => {
    if (!isLoading) {
      if (!isAuthenticated) {
        setActiveTab('account'); // Show login tab for unauthenticated users
        setShowAdminDashboard(false);
        setNeedsArtistSetup(false);
      }
      // Don't automatically change tabs here - let checkUserArtists handle it
    }
  }, [isAuthenticated, isLoading]);

  // Update active tab based on current route
  useEffect(() => {
    console.log('App - Location changed:', location.pathname, location.search);
    
    if (location.pathname.startsWith('/request-feedback/')) {
      // Don't change tab for request feedback routes
      return;
    }

    // Don't override if user needs artist setup and is on account page
    if (needsArtistSetup && location.pathname === '/account') {
      console.log('App - Skipping tab change, needs artist setup');
      return;
    }
    
    // Check for /my-tracks/:publicId pattern
    if (location.pathname.match(/^\/my-tracks\/[a-f0-9-]+$/i)) {
      console.log('App - Setting activeTab to my-tracks');
      setActiveTab('my-tracks');
      return;
    }
    
    const pathToTab: Record<string, TabType> = {
      '/my-tracks': 'my-tracks',
      '/upload': 'upload',
      '/give-feedback': 'give-feedback',
      '/my-reviews': 'my-reviews',
      '/account': 'account',
    };
    
    // Check for exact match first
    let tabFromPath = pathToTab[location.pathname];
    
    // If no exact match, check for parameterized routes
    if (!tabFromPath) {
      if (location.pathname.startsWith('/my-reviews/')) {
        tabFromPath = 'my-reviews';
      }
    }
    
    if (tabFromPath) {
      console.log('App - Setting activeTab to:', tabFromPath);
      setActiveTab(tabFromPath);
    } else {
      console.log('App - No matching tab for pathname:', location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, needsArtistSetup]);
  // Note: location.search is only used for logging, not logic, so excluded from deps

  // Show loading state
  if (isLoading || checkingArtists) {
    return (
      <div className="App">
        <div className="auth-loading">
          {isLoading ? 'Loading...' : 'Checking artist profiles...'}
        </div>
      </div>
    );
  }

  // Show landing page for unauthenticated users on root path
  if (!isAuthenticated && location.pathname === '/') {
    return (
      <div className="App">
        <LandingPage />
        <Footer />
      </div>
    );
  }

  const renderAccountContent = () => {
    if (isAuthenticated && user) {
      return (
        <div className="account-info-container">
          <div className="account-header">
            <h2 className="welcome-message">
              Welcome, {user.firstName}!
            </h2>
          </div>
          
          {/* Artist Profile Management */}
          <ArtistManager />
          
        </div>
      );
    }

    // User is not authenticated - show login/register forms
    if (authView === 'login') {
      return (
        <LoginForm 
          onSwitchToRegister={() => setAuthView('register')} 
          onSwitchToForgotPassword={() => setAuthView('forgot-password')}
        />
      );
    } else if (authView === 'forgot-password') {
      return (
        <ForgotPasswordForm onSwitchToLogin={() => setAuthView('login')} />
      );
    } else {
      return (
        <RegisterForm onSwitchToLogin={() => setAuthView('login')} />
      );
    }
  };

  const renderTabContent = () => {
    // If admin is viewing the dashboard, show it instead of regular content
    if (showAdminDashboard && isAdmin) {
      return <AdminDashboard onExitAdmin={() => setShowAdminDashboard(false)} />;
    }

    // Extract publicId from pathname if on /my-tracks/:publicId route
    const myTracksMatch = location.pathname.match(/^\/my-tracks\/([a-f0-9-]+)$/i);
    const selectedTrackPublicId = myTracksMatch ? myTracksMatch[1] : undefined;

    switch (activeTab) {
      case 'my-tracks':
        return <GetFeedbackTab initialView="tracks" selectedTrackPublicId={selectedTrackPublicId} />;
      case 'upload':
        return <GetFeedbackTab initialView="upload" />;
      case 'give-feedback':
        return <GiveFeedbackTab />;
      case 'my-reviews':
        return <MyReviews />;
      case 'account':
        return renderAccountContent();
      default:
        return null;
    }
  };

  // Show header when logged in
  const showHeader = isAuthenticated;

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    window.history.pushState(null, '', `/${tab}`);
  };

  return (
    <div className="App">
      {/* Show header when logged in */}
      {showHeader && (
        <Header
          activeTab={activeTab}
          onTabChange={handleTabChange}
          isAdmin={isAdmin}
          showAdminDashboard={showAdminDashboard}
          onAdminToggle={() => setShowAdminDashboard(!showAdminDashboard)}
        />
      )}
      <main className="main-content">
        {renderTabContent()}
      </main>
      
      {/* Artist Setup Modal */}
      {isAuthenticated && showArtistModal && (
        <ArtistSetupCard
          artistName={artistName}
          setArtistName={setArtistName}
          artistUsesAI={artistUsesAI}
          setArtistUsesAI={setArtistUsesAI}
          onCreateArtist={createArtist}
          creatingArtist={creatingArtist}
          errorMessage={artistError}
          description="Before you can upload tracks or give feedback, please create your artist profile:"
          showAsModal={true}
          headingText="Set Up Your Artist Profile"
          submitButtonText="Create Artist Profile"
        />
      )}
      
      <Footer />
    </div>
  );
}

function RequestFeedbackWrapper() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Check if user is admin
  const isAdmin = user?.roles?.includes('Admin') || false;

  const handleTabChange = (tab: 'my-tracks' | 'upload' | 'give-feedback' | 'my-reviews' | 'account') => {
    navigate(`/${tab}`);
  };

  return (
    <div className="App">
      {/* Show header when logged in on request feedback pages */}
      {isAuthenticated && (
        <Header
          activeTab="account" // No specific active tab for request feedback
          onTabChange={handleTabChange}
          isAdmin={isAdmin}
          showAdminDashboard={false}
          showRequestFeedbackButton={true}
        />
      )}
      <main className="main-content">
        <RequestFeedback />
      </main>
      <Footer />
    </div>
  );
}

function AppContent() {
  return (
    <Routes>
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/subscriptions" element={<SubscriptionPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/request-feedback/:publicTrackID" element={<RequestFeedbackWrapper />} />
      <Route path="/*" element={<MainApp />} />
    </Routes>
  );
}

function PublicPageLayout({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.roles?.includes('Admin') || false;
  const [authView, setAuthView] = useState<AuthView>('login');
  const [showMobileAuth, setShowMobileAuth] = useState(false);

  const handleTabChange = (tab: 'my-tracks' | 'upload' | 'give-feedback' | 'my-reviews' | 'account') => {
    navigate(`/${tab}`);
  };

  const authPanel = (
    <div className="auth-container">
      <div className="auth-tabs">
        <button
          className={`auth-tab ${authView === 'login' ? 'active' : ''}`}
          onClick={() => setAuthView('login')}
        >
          Login
        </button>
        <button
          className={`auth-tab ${authView === 'register' ? 'active' : ''}`}
          onClick={() => setAuthView('register')}
        >
          Register
        </button>
      </div>
      <div className="auth-form-wrapper">
        {authView === 'login' && (
          <LoginForm
            onSwitchToRegister={() => setAuthView('register')}
            onSwitchToForgotPassword={() => setAuthView('forgot-password')}
          />
        )}
        {authView === 'register' && (
          <RegisterForm onSwitchToLogin={() => setAuthView('login')} />
        )}
        {authView === 'forgot-password' && (
          <ForgotPasswordForm onSwitchToLogin={() => setAuthView('login')} />
        )}
      </div>
    </div>
  );

  return (
    <div className="App">
      {isAuthenticated && (
        <Header
          activeTab="account"
          onTabChange={handleTabChange}
          isAdmin={isAdmin}
          showAdminDashboard={false}
        />
      )}
      <main className="main-content">
        {isAuthenticated ? (
          children
        ) : (
          <div className="hiw-page-layout">
            <div className="hiw-page-content">{children}</div>
            <div className="hiw-page-auth">{authPanel}</div>
          </div>
        )}
      </main>

      {!isAuthenticated && (
        <div className="hiw-mobile-login-header">
          <button
            className="mobile-login-button"
            onClick={() => setShowMobileAuth(true)}
          >
            Login / Register
          </button>
        </div>
      )}

      {!isAuthenticated && showMobileAuth && (
        <div className="mobile-auth-modal">
          <div className="mobile-auth-overlay" onClick={() => setShowMobileAuth(false)} />
          <div className="mobile-auth-content">
            <button className="mobile-auth-close" onClick={() => setShowMobileAuth(false)}>
              ×
            </button>
            <div className="mobile-auth-container">{authPanel}</div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

function HowItWorksPage() {
  return (
    <PublicPageLayout>
      <HowItWorks />
    </PublicPageLayout>
  );
}

function SubscriptionPage() {
  return (
    <PublicPageLayout>
      <SubscriptionManager />
    </PublicPageLayout>
  );
}

function ResetPasswordPage() {
  return (
    <div className="App">
      <main className="main-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <ResetPasswordForm />
      </main>
      <Footer />
    </div>
  );
}

function TermsOfServicePage() {
  return (
    <PublicPageLayout>
      <TermsOfService />
    </PublicPageLayout>
  );
}

function PrivacyPolicyPage() {
  return (
    <PublicPageLayout>
      <PrivacyPolicy />
    </PublicPageLayout>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
